import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import {
  INITIAL_FILTERS,
  normaliseFilters,
  quantise,
} from "../search/supplierFilters";

const PARAM_KEYS = {
  containerOptionId: "option",
  containerTypeId: "type",
  fuelTypeId: "fuel",
  maxVolume: "maxVolume",
  maxPricePerKg: "maxPrice",
  radiusKm: "radius",
};

let lastQuery = "";

const readNumber = (params, key) => {
  const raw = params.get(key);
  if (raw === null || raw === "") return null;

  const parsed = Number(raw);

  return Number.isFinite(parsed) ? parsed : null;
};

export const readHomeUrlState = (params) => {
  const latitude = readNumber(params, "lat");
  const longitude = readNumber(params, "lng");

  const filters = {};

  Object.entries(PARAM_KEYS).forEach(([field, key]) => {
    const value = readNumber(params, key);
    if (value !== null) filters[field] = value;
  });

  return {
    coordinates:
      latitude !== null && longitude !== null ? { latitude, longitude } : null,
    address: params.get("address") || "",
    filters: normaliseFilters({ ...INITIAL_FILTERS, ...filters }),
  };
};

const useHomeUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initial = useRef(null);

  if (!initial.current) {
    const restoring = !searchParams.toString() && Boolean(lastQuery);

    initial.current = readHomeUrlState(
      restoring ? new URLSearchParams(lastQuery) : searchParams
    );
  }

  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;

  const writeState = useCallback(
    ({ coordinates, address, filters }) => {
      const next = new URLSearchParams(paramsRef.current);

      if (coordinates) {
        next.set("lat", String(quantise(coordinates.latitude)));
        next.set("lng", String(quantise(coordinates.longitude)));
      } else {
        next.delete("lat");
        next.delete("lng");
      }

      if (address) next.set("address", address);
      else next.delete("address");

      const applied = normaliseFilters(filters);

      Object.entries(PARAM_KEYS).forEach(([field, key]) => {
        const value = applied[field];

        if (value === null || value === INITIAL_FILTERS[field]) next.delete(key);
        else next.set(key, String(value));
      });

      lastQuery = next.toString();

      if (lastQuery === paramsRef.current.toString()) return;

      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  return useMemo(
    () => ({
      initialCoordinates: initial.current.coordinates,
      initialAddress: initial.current.address,
      initialFilters: initial.current.filters,
      writeState,
    }),
    [writeState]
  );
};

export const useHomeUrlSync = ({ coordinates, address, filters, writeState }) => {
  useEffect(() => {
    writeState({ coordinates, address, filters });
  }, [coordinates, address, filters, writeState]);
};

export default useHomeUrlState;
